import React from 'react';
import { Link } from 'react-router-dom';

import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { organizationSchema, createBreadcrumbSchema } from '../lib/structured-data';
import { track } from '../utils/analytics';
import {
  FileText, Search, Scale, ArrowRight
} from 'lucide-react';

const BOOK_DEMO_URL = 'https://calendly.com/rcarloc029/30min';

function AppSurface() {
  return (
    <div className="bg-[var(--color-csai-paper)] border border-[var(--color-csai-surface)] overflow-hidden" style={{ minHeight: '800px' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-[var(--color-csai-surface)] bg-[var(--color-csai-bg)]">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-[var(--color-csai-surface)]" />
            <div className="w-3 h-3 rounded-full bg-[var(--color-csai-surface)]" />
            <div className="w-3 h-3 rounded-full bg-[var(--color-csai-surface)]" />
          </div>
          <span className="text-xs text-[var(--color-csai-muted)]">Client: Maple Studio Services Ltd.</span>
        </div>
        <span className="text-xs font-medium text-[var(--color-csai-muted)] bg-[var(--color-csai-surface)] px-2 py-1 rounded">Workflow preview</span>
      </div>

      {/* 3-column layout */}
      <div className="flex" style={{ height: 'calc(800px - 57px)' }}>
        {/* Left rail */}
        <div className="w-40 shrink-0 border-r border-[var(--color-csai-surface)] p-4 bg-[var(--color-csai-bg)]">
          <p className="text-xs font-bold text-[var(--color-csai-primary)] mb-4">Orca</p>
          <div className="space-y-2">
            <div className="text-xs text-[var(--color-csai-accent)] font-medium">Workspaces</div>
            <div className="text-xs text-[var(--color-csai-muted)]">Recents</div>
          </div>
          <div className="mt-6 space-y-1">
            {['CRA reassessment', 'Objection draft', 'Research'].map(item => (
              <div key={item} className="text-xs text-[var(--color-csai-muted)] truncate">{item}</div>
            ))}
          </div>
        </div>

        {/* Center panel */}
        <div className="flex-1 p-5 overflow-hidden flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-[var(--color-csai-accent)]">First-pass response outline</p>
            <span className="text-xs font-medium text-[var(--color-csai-primary)] bg-[var(--color-csai-surface)] px-2 py-1 rounded">Draft for CPA review</span>
          </div>
          <div className="space-y-3">
            {[
              { title: 'Overview', bars: [1, 0.8] },
              { title: 'Issues summary', bars: [1, 0.9, 0.7] },
              { title: 'Key facts extracted', bars: [1, 0.75] },
              { title: 'Missing facts', bars: [1, 0.6] },
            ].map(({ title, bars }) => (
              <div key={title} className="bg-[var(--color-csai-surface)] rounded p-3">
                <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-2">{title}</p>
                <div className="space-y-1">
                  {bars.map((w, i) => (
                    <div key={i} className="h-2 bg-[var(--color-csai-bg)] rounded" style={{ width: `${w * 100}%` }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* Additional draft blocks — lower half */}
          <div className="mt-2 pt-3 border-t border-[var(--color-csai-surface)]">
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-2">Source references</p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-3/4 bg-[var(--color-csai-bg)] rounded" />
                <span className="text-[10px] text-[var(--color-csai-muted)]">Tax authority reference</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-2/3 bg-[var(--color-csai-bg)] rounded" />
                <span className="text-[10px] text-[var(--color-csai-muted)]">Review reference</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-1/2 bg-[var(--color-csai-bg)] rounded" />
                <span className="text-[10px] text-[var(--color-csai-muted)]">CRA guidance note</span>
              </div>
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-2">Missing facts</p>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-csai-primary)]" />
                <div className="h-1.5 w-full bg-[var(--color-csai-bg)] rounded" />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-csai-primary)]" />
                <div className="h-1.5 w-5/6 bg-[var(--color-csai-bg)] rounded" />
              </div>
            </div>
          </div>
        </div>

        {/* Right rail */}
        <div className="w-48 shrink-0 border-l border-[var(--color-csai-surface)] p-4 bg-[var(--color-csai-bg)] space-y-4 overflow-hidden">
          <div>
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">Progress</p>
            <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded-full">
              <div className="h-1.5 w-2/3 bg-[var(--color-csai-primary)] rounded-full" />
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">Working folder</p>
            <div className="space-y-1">
              <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded" />
              <div className="h-1.5 w-5/6 bg-[var(--color-csai-surface)] rounded" />
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">Documents</p>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-[var(--color-csai-muted)]" />
                <div className="h-1.5 flex-1 bg-[var(--color-csai-surface)] rounded" />
              </div>
              <div className="flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-[var(--color-csai-muted)]" />
                <div className="h-1.5 flex-1 bg-[var(--color-csai-surface)] rounded" />
              </div>
              <div className="flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-[var(--color-csai-muted)]" />
                <div className="h-1.5 flex-1 bg-[var(--color-csai-surface)] rounded" />
              </div>
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">Relevant source material</p>
            <div className="space-y-1">
              <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded" />
              <div className="h-1.5 w-4/5 bg-[var(--color-csai-surface)] rounded" />
              <div className="h-1.5 w-3/4 bg-[var(--color-csai-surface)] rounded" />
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">Source notes and review references</p>
            <div className="space-y-1">
              <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded" />
              <div className="h-1.5 w-3/5 bg-[var(--color-csai-surface)] rounded" />
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">Reviewer notes</p>
            <div className="space-y-1">
              <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded" />
              <div className="h-1.5 w-4/5 bg-[var(--color-csai-surface)] rounded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HubLanding() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <SEO
        title="Orca - Canadian CPA workbench"
        description="Orca is an AI workbench for Canadian CPAs handling document-heavy tax and client workflows."
        canonical="https://situsai.ca"
        ogImage="https://situsai.ca/logo.svg"
      />
      <JsonLd data={organizationSchema} />
      <JsonLd data={createBreadcrumbSchema('/')} />
      <Header />

      <main className="flex-grow">
        {/* Section A: Hero — Linear-style continuous app surface */}
        <section className="relative overflow-hidden">
          {/* Hero copy — compact, overlaid on app surface */}
          <div className="relative z-10 py-14 sm:py-16 px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-6">
                Canadian CPA workbench
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold mb-4 text-[var(--color-csai-accent)] tracking-tight">
                An AI workbench for Canadian CPAs handling document-heavy tax work
              </h1>
              <p className="text-lg text-[var(--color-csai-muted)] max-w-2xl mx-auto mb-8">
                Orca helps CPAs turn scattered client files and Canadian source material into reviewable draft work, with missing-fact flags, source references, and CPA review boundaries.
              </p>
              <p className="text-sm text-[var(--color-csai-muted)] max-w-md mx-auto mb-6">
                See a 30-minute walkthrough of a document-heavy CPA workflow: packet review, missing facts, draft preparation, and CPA review boundary.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Work email"
                  className="flex-1 h-12 px-4 rounded-sm border border-[var(--color-csai-surface)] bg-white text-[var(--color-csai-accent)] placeholder:text-[var(--color-csai-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-csai-primary)]"
                />
                <a
                  href={BOOK_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track('book_demo_click', { page: 'home', section: 'hero', cta_label: 'Book demo' })}
                  className="h-12 px-6 rounded-sm bg-[var(--color-csai-primary)] text-white font-semibold hover:bg-[var(--color-csai-primary-hover)] transition-colors inline-flex items-center justify-center"
                >
                  Book demo
                </a>
              </div>
            </div>
          </div>

          {/* Large app surface — extends below fold, scrolls to reveal more */}
          {/* Desktop: 90vw, max-w-[1600px], tall; Mobile: cropped h-[420px] */}
          <div className="relative w-full overflow-hidden">
            {/* Mobile cropped preview */}
            <div className="md:hidden h-[420px] overflow-hidden shadow-[0_-8px_40px_rgba(0,0,0,0.12)]">
              <AppSurface />
            </div>
            {/* Desktop: full continuous surface */}
            <div className="hidden md:block w-full shadow-[0_-8px_60px_rgba(0,0,0,0.12)]">
              <div className="w-[90vw] max-w-[1600px] mx-auto">
                <AppSurface />
              </div>
            </div>
          </div>
        </section>

        {/* Section B: Category Transition — Not Another Chatbot */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--color-csai-accent)] mb-6 tracking-tight leading-tight">Not another chatbot. A workbench for review.</h2>
            <p className="text-lg text-[var(--color-csai-muted)] max-w-xl mx-auto">
              CPA work depends on documents, professional judgment, source context, and reviewer handoff. The interface should keep those pieces visible.
            </p>
          </div>

          {/* Process flow */}
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-4 sm:gap-8">
              {[
                { label: 'Packet', icon: FileText },
                { label: 'Gaps', icon: Search },
                { label: 'Draft', icon: Scale },
                { label: 'Review', icon: ArrowRight },
              ].map(({ label, icon: Icon }, i) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full border border-[var(--color-csai-surface)] bg-white flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[var(--color-csai-primary)]" />
                    </div>
                    <span className="text-xs text-[var(--color-csai-muted)] font-medium">{label}</span>
                  </div>
                  {i < 3 && (
                    <div className="w-8 sm:w-16 h-px bg-[var(--color-csai-surface)] shrink-0 mt-[-1rem]" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        {/* Section C: Problem — Make CPAs feel seen */}
        <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-paper)]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-6 tracking-tight leading-tight">
              Clients do not send clean tax questions.
            </h2>
            <p className="text-lg text-[var(--color-csai-muted)] max-w-xl mx-auto mb-8">
              They send PDFs, emails, notices, invoices, missing facts, and deadlines.
            </p>
            <p className="text-base text-[var(--color-csai-muted)] max-w-lg mx-auto">
              Deadline pressure. Missing evidence. Source confidence. Review and workpaper demands. This is the real workflow mess generic AI cannot help with.
            </p>
          </div>
        </section>

        {/* Section D: Workflow Section */}
        <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-6 tracking-tight leading-tight">
              From document packet to reviewable draft work.
            </h2>
            <p className="text-lg text-[var(--color-csai-muted)]">
              Orca helps you move through a document-heavy file systematically, with source context and missing-fact flags at each step.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="space-y-4">
              {[
                { n: '1', label: 'Collect client documents' },
                { n: '2', label: 'Ask tax or workflow questions in CPA language' },
                { n: '3', label: 'Inspect relevant Canadian source material and workspace files' },
                { n: '4', label: 'Extract facts and identify missing evidence' },
                { n: '5', label: 'Draft first-pass workpapers, memos, letters, CRA responses, or review notes' },
                { n: '6', label: 'Review source references, assumptions, and CPA boundaries' },
              ].map(({ n, label }) => (
                <div key={n} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)]">
                  <div className="w-7 h-7 rounded-full border border-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-[var(--color-csai-primary)]">{n}</span>
                  </div>
                  <p className="text-sm text-[var(--color-csai-accent)] pt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scene 1: Packet */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Product panel left */}
              <div>
                <p className="text-xs font-semibold text-[var(--color-csai-primary)] tracking-widest uppercase mb-4">Packet review</p>
                <div className="bg-white rounded-xl border border-[var(--color-csai-surface)] shadow-sm overflow-hidden">
                  {/* Top bar */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-csai-surface)] bg-[var(--color-csai-bg)]">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                    </div>
                    <span className="ml-2 text-xs text-[var(--color-csai-muted)]">Working folder</span>
                  </div>
                  {/* File list */}
                  <div className="p-4 space-y-2">
                    {[
                      'Maple packet.pdf',
                      'Engagement letter.pdf',
                      'Prior year statements.pdf',
                      'October invoice.pdf',
                    ].map(file => (
                      <div key={file} className="flex items-center gap-2 py-1.5 border-b border-[var(--color-csai-bg)] last:border-0">
                        <FileText className="w-3.5 h-3.5 text-[var(--color-csai-muted)] shrink-0" />
                        <span className="text-xs text-[var(--color-csai-accent)]">{file}</span>
                      </div>
                    ))}
                    <div className="pt-2 mt-2 border-t border-[var(--color-csai-surface)]">
                      <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">Relevant source material</p>
                      <div className="space-y-1">
                        <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded" />
                        <div className="h-1.5 w-4/5 bg-[var(--color-csai-surface)] rounded" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Copy right */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[var(--color-csai-accent)] mb-4">
                  Start with the work folder
                </h3>
                <p className="text-[var(--color-csai-muted)] text-lg leading-relaxed">
                  Client documents, notes, and relevant materials stay in view before the draft starts.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Scene 2: Gaps */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Copy left */}
              <div>
                <p className="text-xs font-semibold text-[var(--color-csai-primary)] tracking-widest uppercase mb-4">Gap analysis</p>
                <h3 className="text-2xl sm:text-3xl font-bold text-[var(--color-csai-accent)] mb-4">
                  Find what still needs review
                </h3>
                <p className="text-[var(--color-csai-muted)] text-lg leading-relaxed">
                  The workflow should surface missing facts, assumptions, caveats, and reviewer questions before a CPA relies on the work.
                </p>
              </div>
              {/* Product panel right */}
              <div>
                <div className="bg-white rounded-xl border border-[var(--color-csai-surface)] shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-csai-surface)] bg-[var(--color-csai-bg)]">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                    </div>
                    <span className="ml-2 text-xs text-[var(--color-csai-muted)]">Missing facts and notes</span>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-[var(--color-csai-surface)]">
                    {[
                      { label: 'Missing facts', bars: [1, 0.7, 0.5] },
                      { label: 'Assumptions / Caveats', bars: [1, 0.6] },
                      { label: 'Reviewer notes', bars: [1, 0.8, 0.4] },
                    ].map(({ label, bars }) => (
                      <div key={label} className="p-3">
                        <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-2">{label}</p>
                        <div className="space-y-1">
                          {bars.map((w, i) => (
                            <div key={i} className="h-1.5 bg-[var(--color-csai-surface)] rounded" style={{ width: `${w * 100}%` }} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scene 3: Draft */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Product panel left */}
              <div>
                <p className="text-xs font-semibold text-[var(--color-csai-primary)] tracking-widest uppercase mb-4">Draft preparation</p>
                <div className="bg-white rounded-xl border border-[var(--color-csai-surface)] shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-csai-surface)] bg-[var(--color-csai-bg)]">
                    <span className="text-xs text-[var(--color-csai-muted)]">Response outline</span>
                    <span className="text-xs font-medium text-[var(--color-csai-primary)] bg-[var(--color-csai-surface)] px-2 py-0.5 rounded">Draft for CPA review</span>
                  </div>
                  <div className="p-4 space-y-3">
                    {['Background', 'Position summary', 'Detailed outline', 'Next steps / Requests'].map(section => (
                      <div key={section}>
                        <p className="text-xs font-semibold text-[var(--color-csai-accent)] mb-1">{section}</p>
                        <div className="space-y-1">
                          <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded" />
                          <div className="h-1.5 w-5/6 bg-[var(--color-csai-surface)] rounded" />
                          <div className="h-1.5 w-3/4 bg-[var(--color-csai-surface)] rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Copy right */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[var(--color-csai-accent)] mb-4">
                  Shape a first-pass outline
                </h3>
                <p className="text-[var(--color-csai-muted)] text-lg leading-relaxed">
                  Orca is designed around reviewable outlines, workpapers, memos, and response drafts under CPA review.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Scene 4: Review */}
        <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Copy left */}
              <div>
                <p className="text-xs font-semibold text-[var(--color-csai-primary)] tracking-widest uppercase mb-4">CPA review</p>
                <h3 className="text-2xl sm:text-3xl font-bold text-[var(--color-csai-accent)] mb-4">
                  Keep references beside the work
                </h3>
                <p className="text-[var(--color-csai-muted)] text-lg leading-relaxed">
                  Relevant source material, source notes, and reviewer references should stay connected to the draft.
                </p>
              </div>
              {/* Product panel right */}
              <div>
                <div className="bg-white rounded-xl border border-[var(--color-csai-surface)] shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-csai-surface)] bg-[var(--color-csai-bg)]">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                      <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-csai-surface)]" />
                    </div>
                    <span className="ml-2 text-xs text-[var(--color-csai-muted)]">Review references</span>
                  </div>
                  <div className="p-4 space-y-3">
                    {[
                      'Relevant source material',
                      'Source notes and review references',
                      'Reviewer notes',
                    ].map(item => (
                      <div key={item}>
                        <p className="text-xs font-medium text-[var(--color-csai-muted)] mb-1">{item}</p>
                        <div className="space-y-1">
                          <div className="h-1.5 w-full bg-[var(--color-csai-surface)] rounded" />
                          <div className="h-1.5 w-4/5 bg-[var(--color-csai-surface)] rounded" />
                          <div className="h-1.5 w-3/4 bg-[var(--color-csai-surface)] rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section E: Workbench vs Chatbot */}
        <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-paper)]">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-8 tracking-tight leading-tight">
              Workbench, not chatbot.
            </h2>
            <div className="space-y-6">
              {[
                { contrast: 'Generic AI', orca: 'Orca' },
                { contrast: 'Gives answers in a thread', orca: 'Keeps the work reviewable in a workspace' },
                { contrast: 'Prompt history', orca: 'Document context' },
                { contrast: 'Generic response', orca: 'Reviewable draft work' },
                { contrast: 'One-off answer', orca: 'Source refs, missing facts, and reviewer notes' },
              ].map(({ contrast, orca }, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex-1 text-sm text-[var(--color-csai-muted)]">{contrast}</div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-[var(--color-csai-surface)]" />
                  </div>
                  <div className="flex-1 text-sm font-medium text-[var(--color-csai-accent)]">{orca}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section F: CRA/Reassessment Wedge */}
        <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-6 tracking-tight leading-tight">
              Built around CRA reassessment deadline support.
            </h2>
            <p className="text-lg text-[var(--color-csai-muted)]">
              When a client drops a CRA notice on your desk with days to respond, Orca helps you work through the packet, not around it.
            </p>
          </div>
          <div className="max-w-2xl mx-auto grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              'CRA notice',
              'Client packet',
              'Missing evidence',
              'Draft response outline',
              'Review notes',
              'Source references',
            ].map(label => (
              <div key={label} className="p-3 bg-white rounded-lg border border-[var(--color-csai-surface)] text-sm text-[var(--color-csai-accent)] text-center">
                {label}
              </div>
            ))}
          </div>
        </section>

        {/* Section G: Trust / Review */}
        <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-paper)]">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-6 tracking-tight leading-tight">
              AI output is only useful if a CPA can inspect it.
            </h2>
            <p className="text-lg text-[var(--color-csai-muted)]">
              Drafts are not decisions. Missing facts stay visible. Source references stay inspectable. Reviewer notes and caveats are part of the workflow. CPA review remains the boundary.
            </p>
          </div>
          <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'Drafts are not decisions',
              'Missing facts stay visible',
              'Source references stay inspectable',
              'Reviewer notes are part of workflow',
              'CPA review remains the boundary',
            ].map(label => (
              <div key={label} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)]">
                <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-csai-primary)] mt-2 shrink-0" />
                <span className="text-sm text-[var(--color-csai-accent)]">{label}</span>
              </div>
            ))}
          </div>
          <div className="max-w-2xl mx-auto mt-8 p-5 bg-white rounded-lg border border-[var(--color-csai-surface)] text-center">
            <p className="text-sm text-[var(--color-csai-muted)]">
              Security and data-handling details are documented separately for review before any firm uses Orca with client files.
            </p>
            <p className="text-xs text-[var(--color-csai-muted)] mt-2">
              <a href="/security" className="underline hover:text-[var(--color-csai-primary)]">Review security notes</a>
            </p>
          </div>
        </section>

        {/* Section G2: How Orca Works */}
        <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-bg)]">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-6 tracking-tight leading-tight">
              How Orca works through a document-heavy file
            </h2>
            <p className="text-lg text-[var(--color-csai-muted)]">
              From document packet to reviewable draft — under CPA control at every step.
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)] mb-4">
              <div className="w-7 h-7 rounded-full border border-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-[var(--color-csai-primary)]">1</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--color-csai-accent)] mb-1">Collect client documents</p>
                <p className="text-xs text-[var(--color-csai-muted)]">PDFs, emails, CRA notices, invoices, and notes stay organized in the workspace.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)] mb-4">
              <div className="w-7 h-7 rounded-full border border-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-[var(--color-csai-primary)]">2</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--color-csai-accent)] mb-1">Ask tax or workflow questions in CPA language</p>
                <p className="text-xs text-[var(--color-csai-muted)]">Orca uses Canadian tax terminology and context to respond to workflow questions, not just keywords.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)] mb-4">
              <div className="w-7 h-7 rounded-full border border-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-[var(--color-csai-primary)]">3</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--color-csai-accent)] mb-1">Inspect relevant source material and workspace files</p>
                <p className="text-xs text-[var(--color-csai-muted)]">Canadian source material and client files are surfaced together so you can verify context.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)] mb-4">
              <div className="w-7 h-7 rounded-full border border-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-[var(--color-csai-primary)]">4</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--color-csai-accent)] mb-1">Extract facts and identify missing evidence</p>
                <p className="text-xs text-[var(--color-csai-muted)]">Key facts are extracted and missing evidence is flagged before you rely on the draft.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)] mb-4">
              <div className="w-7 h-7 rounded-full border border-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-[var(--color-csai-primary)]">5</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--color-csai-accent)] mb-1">Draft first-pass workpapers, memos, letters, or CRA responses</p>
                <p className="text-xs text-[var(--color-csai-muted)]">Drafts are labeled as first-pass and require CPA review before any use.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-white rounded-lg border border-[var(--color-csai-surface)]">
              <div className="w-7 h-7 rounded-full border border-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-[var(--color-csai-primary)]">6</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--color-csai-accent)] mb-1">Review source references, assumptions, and CPA boundaries</p>
                <p className="text-xs text-[var(--color-csai-muted)]">Source notes and reviewer references stay visible so you can challenge or confirm before signing off.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Where Orca helps most */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-paper)]">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-4">
              Where Orca helps most
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: FileText, label: 'CRA reassessment / objection response' },
                { icon: Search, label: 'Tax research memo' },
                { icon: Scale, label: 'Workpaper or review file' },
                { icon: ArrowRight, label: 'Client document Q&A' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-[var(--color-csai-surface)]">
                  <div className="w-8 h-8 rounded-lg bg-[var(--color-csai-surface)] flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-[var(--color-csai-primary)]" />
                  </div>
                  <span className="text-sm text-[var(--color-csai-accent)] text-left">{label}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-[var(--color-csai-muted)] text-center mt-6 italic">
              These workflows show where Orca supports document-heavy CPA review.
            </p>
          </div>
        </section>

        {/* Section: Final CTA */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-[var(--color-csai-primary)] text-white">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">See how Orca supports document-heavy CPA workflow review.</h2>
            <p className="text-[#a3b8cc] mb-6 text-base">
              See a 30-minute walkthrough of a document-heavy CPA workflow: packet review, missing facts, draft preparation, and CPA review boundary.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 max-w-md mx-auto mb-4">
              <input
                type="email"
                placeholder="Work email"
                className="flex-1 h-12 px-4 rounded-sm border border-white/20 bg-white/10 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/40"
              />
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('book_demo_click', { page: 'home', section: 'final_cta', cta_label: 'Book demo' })}
                className="h-12 px-6 rounded-sm bg-white text-[var(--color-csai-primary)] font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center"
              >
                Book demo
              </a>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
